import { useState, type ReactNode } from 'react'
import { BellRing, History } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { Wordmark } from './wordmark'
import { LiveStats } from './live-stats'
import { UserMenu } from './user-menu'
import { ThemeToggle } from './theme-toggle'
import { LocaleToggle } from './locale-toggle'
import { Button } from '#/components/ui/button'
import { PlanDrawer } from '#/components/plan/plan-drawer'
import { useHydratedSession } from '#/lib/auth-client'
import { useInbox } from '#/lib/api-inbox'
import { cn } from '#/lib/utils'
import { m } from '#/paraglide/messages'

/**
 * App header, h-14. `center` (e.g. a stepper) is absolutely centred and
 * hidden on mobile, as in spec/05 §3. `left` replaces the live stats slot.
 * Signed-in users always get a "Saved plans" button that opens the plan drawer,
 * so a plan is reachable from any page (even mid-generation).
 * `compact` hides the locale/theme toggles on phones — used by dense workspace headers.
 */
export function AppHeader({
  center,
  left,
  right,
  className,
  showStats = true,
  compact = false,
}: {
  center?: ReactNode
  left?: ReactNode
  right?: ReactNode
  className?: string
  showStats?: boolean
  compact?: boolean
}) {
  const { data: session } = useHydratedSession()
  const [drawer, setDrawer] = useState(false)
  const signedIn = Boolean(session?.user)
  // Inspirasi F1: the attention badge — how many things wait for the user, refreshed every minute
  const { data: inbox } = useInbox(signedIn)
  const waiting = inbox?.counts.blocked ?? 0
  return (
    <header className={cn('relative flex h-14 shrink-0 items-center justify-between border-b bg-background px-3 sm:px-4', className)}>
      <div className="flex min-w-0 items-center gap-3 sm:gap-4">
        <Wordmark compact />
        {left ?? (showStats ? <LiveStats /> : null)}
      </div>
      {center ? <div className="absolute left-1/2 hidden -translate-x-1/2 sm:block">{center}</div> : null}
      <div className="flex shrink-0 items-center gap-1">
        {right}
        {signedIn ? (
          <Button asChild size="icon" variant="ghost" className="relative size-8" aria-label={m.inbox_header_badge({ n: waiting })} title={m.inbox_header_badge({ n: waiting })}>
            <Link to="/inbox" data-testid="inbox-badge" data-count={waiting}>
              <BellRing className={cn('size-4', waiting > 0 && 'text-status-progress')} />
              {waiting > 0 ? <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-status-progress px-1 text-[10px] font-semibold leading-none text-white">{waiting > 9 ? '9+' : waiting}</span> : null}
            </Link>
          </Button>
        ) : null}
        {signedIn ? (
          <Button size="icon" variant="ghost" className="size-8" onClick={() => setDrawer(true)} aria-label={m.ws_saved_plans()} title={m.ws_saved_plans()}>
            <History className="size-4" />
          </Button>
        ) : null}
        <div className={cn('flex items-center gap-1', compact && 'hidden sm:flex')}>
          <LocaleToggle />
          <ThemeToggle />
        </div>
        <div className="ml-1">
          <UserMenu />
        </div>
      </div>
      {signedIn ? <PlanDrawer open={drawer} onOpenChange={setDrawer} /> : null}
    </header>
  )
}
